import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  BokkzIbDataService,
  BuitenlandseBronheffing,
  IncomeTaxEntity,
  IngehoudenDividendbelasting
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {debounceTime, filter, map, Observable, pairwise, shareReplay, startWith, Subscription, take} from "rxjs";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {optionsLandcodesZonderNLD} from "../../gedeelde-componenten/gedeelde-componenten.component";

type DivFormGroup = FormGroup<{
  rekeningNummer: FormControl<string | null>;
  landCode: FormControl<string | null>;
  buitenlandseRente: FormControl<number | null>;
  bronbelastingOpRente: FormControl<number | null>;
  buitenlandsDividendbox2: FormControl<number | null>;
  bronbelastingOpDividendbox2: FormControl<number | null>;
  buitenlandsDividendbox3: FormControl<number | null>;
  bronbelastingOpDividendbox3: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-buitenlandse-bronheffing',
  templateUrl: './buitenlandse-bronheffing.component.html',
  styleUrls: ['./buitenlandse-bronheffing.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class BuitenlandseBronheffingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesZonderNLD;

  public formGroup = new FormGroup({
    belasting: new FormArray<DivFormGroup>([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(entity => {
      this.formGroup.reset({}, {emitEvent: false});
      this.updateForm(entity?.aangifte?.voorheffingen?.buitenlandseBronheffing || []);
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    this.subscribeToBedragChanges(this.bronheffingBtl);
  }

  private subscribeToBedragChanges(formArray: FormArray<DivFormGroup>): void {
    const extractBedragen = () => formArray.getRawValue().map(item => ({
      bronbelastingOpDividendbox2: item.bronbelastingOpDividendbox2,
      bronbelastingOpDividendbox3: item.bronbelastingOpDividendbox3,
    }));

    this.changeSubscriptions.add(
      formArray.valueChanges.pipe(
        debounceTime(500),
        map(() => this.saveForm()),
        map(() => extractBedragen()),
        startWith(extractBedragen()),
        pairwise(),
        filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  get bronheffingBtl(): FormArray<DivFormGroup> {
    return this.formGroup.controls.belasting;
  }

  addRij(): void {
    this.bronheffingBtl.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRij(index: number): void {
    this.bronheffingBtl.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false})
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
    this.subscribeToChanges();
  }

  createFormGroup(item?: BuitenlandseBronheffing): FormGroup {
    return new FormGroup({
      rekeningNummer: new FormControl<string | null>(item?.rekeningNummer ?? null),
      landCode: new FormControl<string | null>(item?.landCode ?? null),
      buitenlandsDividendbox2: new FormControl<number | null>(item?.buitenlandsDividendbox2 ?? null),
      bronbelastingOpDividendbox2: new FormControl<number | null>(item?.bronbelastingOpDividendbox2 ?? null),
      buitenlandsDividendbox3: new FormControl<number | null>(item?.buitenlandsDividendbox3 ?? null),
      bronbelastingOpDividendbox3: new FormControl<number | null>(item?.bronbelastingOpDividendbox3 ?? null),
    })
  }


  private updateForm(data : BuitenlandseBronheffing[]): void {
    const lijst = data || [];
    const formArray = this.bronheffingBtl;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({ emitEvent: false });

    const items: IngehoudenDividendbelasting[] = this.formGroup.getRawValue().belasting
      .filter(item => item.landCode !== null && (item.bronbelastingOpDividendbox3 !== 0 || item.bronbelastingOpDividendbox2 !== 0))
      .map(item => ({
        rekeningNummer: item.rekeningNummer ?? undefined,
        landCode: item.landCode ?? undefined,
        buitenlandsDividendbox2: item.buitenlandsDividendbox2 ?? undefined,
        bronbelastingOpDividendbox2: item.bronbelastingOpDividendbox2 ?? undefined,
        buitenlandsDividendbox3: item.buitenlandsDividendbox3 ?? undefined,
        bronbelastingOpDividendbox3: item.bronbelastingOpDividendbox3 ?? undefined,
      }));

    this.dataService.patchIncomeTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          voorheffingen: {
            ...currentEntity.aangifte.voorheffingen,
            buitenlandseBronheffing: items
          }
        },
      } as IncomeTaxEntity;
    });
  }
}
